import { useEffect, useState } from "react"
import { Link } from "@inertiajs/react"
import Cart, { type CartData } from "@/components/unsf/Cart"

interface NavbarProps {
  isHome: boolean
  logoWhite: string
  logoBlack: string
  labels: { store: string; about: string; cart: string }
  urls: { root: string; store: string; about: string }
  locale: { current: string; other: string; switchUrl: string }
  csrfToken: string
  cart: CartData
}

export default function Navbar({
  isHome,
  logoWhite,
  logoBlack,
  labels,
  urls,
  locale,
  csrfToken,
  cart,
}: NavbarProps) {
  const [scrolledPastHero, setScrolledPastHero] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [cartOpen, setCartOpen] = useState(false)

  useEffect(() => {
    if (!isHome) return

    const handleScroll = () => {
      const threshold = window.innerHeight - 100
      setScrolledPastHero(window.scrollY > threshold)
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isHome])

  useEffect(() => {
    if (!mobileOpen) return

    const original = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = original
    }
  }, [mobileOpen])

  // Auto-open once when the server signals the first item was just added.
  useEffect(() => {
    if (cart.auto_open) setCartOpen(true)
  }, [cart.auto_open])

  const toggleCart = () => {
    setCartOpen((prev) => !prev)
    setMobileOpen(false)
  }

  const logoFile = isHome ? logoWhite : logoBlack
  const textColor = isHome ? "text-white" : "text-black"
  const borderColor = isHome
    ? scrolledPastHero
      ? "border-transparent"
      : "border-white"
    : "border-black"
  const bgClass = isHome
    ? scrolledPastHero
      ? "bg-black"
      : ""
    : "bg-white"
  const mobileBg = isHome ? "bg-transparent" : "bg-white"

  return (
    <>
      {/* Desktop navbar */}
      <div className="hidden md:block">
        <div
          className={`w-full fixed top-0 z-50 transition-colors duration-300 ${bgClass}`}
        >
          <div className="mx-12 mt-8">
            <div
              className={`flex justify-between pb-2 border-b-2 ${borderColor}`}
            >
              <Link href={urls.root}>
                <img src={logoFile} alt="UNSF" className="h-12" />
              </Link>

              <div className="flex items-end gap-10">
                <a
                  href={urls.store}
                  className={`font-bold ${textColor} text-base font-unsf-title hover:opacity-80 transition-opacity`}
                >
                  {labels.store}
                </a>
                <a
                  href={urls.about}
                  className={`font-bold ${textColor} text-base font-unsf-title hover:opacity-80 transition-opacity`}
                >
                  {labels.about}
                </a>
                <button
                  type="button"
                  onClick={toggleCart}
                  className={`${textColor} text-base font-bold font-unsf-title hover:opacity-80 transition-opacity relative cursor-pointer`}
                >
                  {labels.cart}
                  {cart.count > 0 && (
                    <span className="absolute -top-2 -right-4 bg-light text-contrast text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {cart.count}
                    </span>
                  )}
                </button>

                <form
                  method="post"
                  action={locale.switchUrl}
                  className="inline"
                >
                  <input type="hidden" name="_method" value="patch" />
                  <input
                    type="hidden"
                    name="authenticity_token"
                    value={csrfToken}
                  />
                  <button
                    type="submit"
                    className={`font-bold ${textColor} text-base font-unsf-title hover:opacity-80 transition-opacity uppercase`}
                  >
                    {locale.other}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navbar */}
      <div className="md:hidden">
        <div className={`fixed top-0 left-0 right-0 z-50 ${mobileBg}`}>
          <div className="flex justify-between items-center px-4 py-4">
            <Link href={urls.root}>
              <img src={logoFile} alt="UNSF" className="h-6" />
            </Link>

            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={toggleCart}
                className={`${textColor} relative cursor-pointer`}
                aria-label={labels.cart}
              >
                <i className="fa-solid fa-shopping-bag" />
                {cart.count > 0 && (
                  <span className="absolute -top-2 -right-2 bg-contrast text-light text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {cart.count}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="cursor-pointer"
                aria-label="Open menu"
              >
                <i className={`fa-solid fa-bars text-xl ${textColor}`} />
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 bg-white flex flex-col">
            <div className="flex justify-between items-center px-4 py-4 border-b">
              <Link href={urls.root}>
                <img src={logoBlack} alt="UNSF" className="h-6" />
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="cursor-pointer"
                aria-label="Close menu"
              >
                <i className="fa-solid fa-xmark text-black text-2xl" />
              </button>
            </div>

            <div className="flex flex-col items-center justify-center flex-1 gap-8">
              <a
                href={urls.store}
                className="text-3xl font-bold text-black"
              >
                {labels.store}
              </a>
              <a
                href={urls.about}
                className="text-3xl font-bold text-black"
              >
                {labels.about}
              </a>
              <button
                type="button"
                onClick={toggleCart}
                className="text-3xl font-bold text-black cursor-pointer"
              >
                {labels.cart}
              </button>

              <form method="post" action={locale.switchUrl}>
                <input type="hidden" name="_method" value="patch" />
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={csrfToken}
                />
                <button
                  type="submit"
                  className="text-xl font-bold text-black font-unsf-title uppercase"
                >
                  {locale.other}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      <Cart open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} />
    </>
  )
}
